import { BACKEND_URL, STRAPI_TOKEN } from "src/pages/DashboardPage/gop/api/config";

const strapiHeaders = {
    Authorization: `Bearer ${STRAPI_TOKEN}`,
    "Content-Type": "application/json",
};

const QUESTIONS_ENDPOINT: Record<string, string> = {
    DeviationReport: "/api/mt-5-why-rate-question-deviation-reports",
    default: "/api/mt-5-why-rate-questions",
};

const SETTINGS_ENDPOINT: Record<string, string> = {
    DeviationReport: "/api/mt-deviation-report-settings",
    default: "/api/mt-5-way-rate-settings",
};

export type RateQuestion = {
    id: number;
    documentId?: string;
    Title: string;
    Description?: string;
    SortOrder: number;
    WeightYes: number;
    WeightNo: number;
    QuestionBlock: string;
    QuestionBlockSortOrder: number;
    IsHidden?: boolean;
};

export type RateAnswerPayload = {
    Question: { connect: [{ documentId: string | number }] };
    Answer: "Да" | "Нет";
};

export type ExistingRateRecord = {
    id: number;
    documentId: string;
    TotalWeight: number;
    RateDate?: string;
    RateUser?: {
        id: number;
        email: string;
        Name?: string;
        username?: string;
        PersonalNumber?: string;
        PictureUrl?: { url: string };
    };
    Answers?: { Question?: { id: number }; Answer: string }[];
    AnswersDeviation?: { Question?: { id: number }; Answer: string }[];
};

export type SaveRateParams = {
    cardDocumentId: string;
    userDocumentId: string;
    rateDate: string;
    totalWeight: number;
    answers: RateAnswerPayload[];
    isDeviationReport: boolean;
    existingRateDocumentId: string | null;
};

export type RatingColorThreshold = {
    greenFromPercent: number;
    yellowFromPercent: number;
    redFromPercent: number;
};

export const DEFAULT_RATING_COLOR_THRESHOLD: RatingColorThreshold = {
    greenFromPercent: 90,
    yellowFromPercent: 60,
    redFromPercent: 0,
};

let ratingColorThresholdCache: RatingColorThreshold | null = null;
let ratingColorThresholdPromise: Promise<RatingColorThreshold> | null = null;

export const fetchRatingColorThreshold = async (): Promise<RatingColorThreshold> => {
    if (ratingColorThresholdCache) {
        return ratingColorThresholdCache;
    }

    if (!ratingColorThresholdPromise) {
        ratingColorThresholdPromise = (async () => {
            try {
                const res = await fetch(
                    `${BACKEND_URL}/api/mt-rating-color-threshold`,
                    { headers: strapiHeaders, credentials: "include" },
                );
                if (!res.ok) {
                    console.error("[fetchRatingColorThreshold] HTTP error", res.status, await res.text());
                    return DEFAULT_RATING_COLOR_THRESHOLD;
                }
                const json = await res.json();
                if (json?.error) {
                    console.error("[fetchRatingColorThreshold] Backend error", json.error);
                }
                const raw = Array.isArray(json?.data) ? json.data[0] : json?.data;
                if (!raw) {
                    return DEFAULT_RATING_COLOR_THRESHOLD;
                }

                const threshold: RatingColorThreshold = {
                    greenFromPercent: Number(raw.GreenFromPercent ?? DEFAULT_RATING_COLOR_THRESHOLD.greenFromPercent),
                    yellowFromPercent: Number(raw.YellowFromPercent ?? DEFAULT_RATING_COLOR_THRESHOLD.yellowFromPercent),
                    redFromPercent: Number(raw.RedFromPercent ?? DEFAULT_RATING_COLOR_THRESHOLD.redFromPercent),
                };
                ratingColorThresholdCache = threshold;
                return threshold;
            } catch (error) {
                console.error("[fetchRatingColorThreshold]", error);
                return DEFAULT_RATING_COLOR_THRESHOLD;
            }
        })();
    }

    return ratingColorThresholdPromise;
};

export const fetchRateSettingsDescription = async (cardType: string): Promise<string> => {
    const endpoint = SETTINGS_ENDPOINT[cardType] ?? SETTINGS_ENDPOINT.default;
    try {
        const res = await fetch(
            `${BACKEND_URL}${endpoint}`,
            { headers: strapiHeaders, credentials: "include" },
        );
        if (!res.ok) {
            console.error("[fetchRateSettingsDescription] HTTP error", res.status, await res.text());
            return "";
        }
        const json = await res.json();
        if (json?.error) {
            console.error("[fetchRateSettingsDescription] Backend error", json.error);
        }
        const raw = Array.isArray(json?.data) ? json.data[0] : json?.data;
        return raw?.Description ?? "";
    } catch (error) {
        console.error("[fetchRateSettingsDescription]", error);
        return "";
    }
};

export const fetchRateQuestions = async (cardType: string): Promise<RateQuestion[]> => {
    const endpoint = QUESTIONS_ENDPOINT[cardType] ?? QUESTIONS_ENDPOINT.default;
    const res = await fetch(
        `${BACKEND_URL}${endpoint}?populate[QuestionBlock][fields][0]=Title&populate[QuestionBlock][fields][1]=SortOrder`,
        { headers: strapiHeaders, credentials: "include" },
    );
    if (!res.ok) {
        console.error("[fetchRateQuestions] HTTP error", res.status, await res.text());
        return [];
    }
    const json = await res.json();
    if (json?.error) console.error("[fetchRateQuestions] Backend error", json.error);
    return (json?.data ?? [])
        .map((item: any) => {
            const relationBlock = item.QuestionBlock ?? null;
            const blockTitle = typeof relationBlock === "string" ? relationBlock : relationBlock?.Title ?? "";
            const blockSortOrder = typeof relationBlock === "string" ? 0 : relationBlock?.SortOrder ?? 0;

            return {
                id: item.id,
                documentId: item.documentId,
                Title: item.Title,
                Description: item.Description,
                SortOrder: item.SortOrder,
                WeightYes: item.WeightYes,
                WeightNo: item.WeightNo,
                QuestionBlock: blockTitle,
                QuestionBlockSortOrder: blockSortOrder,
                IsHidden: item.IsHidden ?? false,
            };
        })
        .sort((a: RateQuestion, b: RateQuestion) => {
            const blockSortDiff = a.QuestionBlockSortOrder - b.QuestionBlockSortOrder;
            if (blockSortDiff !== 0) return blockSortDiff;

            const blockTitleDiff = a.QuestionBlock.localeCompare(b.QuestionBlock, "ru");
            if (blockTitleDiff !== 0) return blockTitleDiff;

            const questionSortDiff = a.SortOrder - b.SortOrder;
            if (questionSortDiff !== 0) return questionSortDiff;

            return a.Title.localeCompare(b.Title, "ru");
        });
};

export const fetchExistingRate = async (rateDocumentId: string): Promise<ExistingRateRecord | null> => {
    const res = await fetch(
        `${BACKEND_URL}/api/mt-5-why-rates/${rateDocumentId}` +
        `?populate[RateUser][fields][0]=id` +
        `&populate[RateUser][fields][1]=Name` +
        `&populate[RateUser][fields][2]=email` +
        `&populate[RateUser][fields][3]=PersonalNumber` +
        `&populate[RateUser][populate][PictureUrl][fields][0]=url` +
        `&populate[Answers][populate][Question][fields][0]=id` +
        `&populate[AnswersDeviation][populate][Question][fields][0]=id`,
        { headers: strapiHeaders, credentials: "include" },
    );
    if (!res.ok) {
        console.error("[fetchExistingRate] HTTP error", res.status, await res.text());
        return null;
    }
    const json = await res.json();
    if (json?.error) console.error("[fetchExistingRate] Backend error", json.error);
    return json?.data ?? null;
};

export const fetchRateByCardId = async (
    card5WayID: number,
): Promise<{ id: number; documentId: string; TotalWeight: number } | null> => {
    const res = await fetch(
        `${BACKEND_URL}/api/mt-5-why-rates?filters[Mt5Way][id][$eq]=${card5WayID}&pagination[pageSize]=1`,
        { headers: strapiHeaders, credentials: "include" },
    );
    if (!res.ok) {
        console.error("[fetchRateByCardId] HTTP error", res.status, await res.text());
        return null;
    }
    const json = await res.json();
    if (json?.error) console.error("[fetchRateByCardId] Backend error", json.error);
    return json?.data?.[0] ?? null;
};

export const fetchCardDocumentId = async (card5WayID: number): Promise<string> => {
    const res = await fetch(
        `${BACKEND_URL}/api/mt-5-ways?filters[id][$eq]=${card5WayID}&fields[0]=id&pagination[pageSize]=1`,
        { headers: strapiHeaders, credentials: "include" },
    );
    if (!res.ok) {
        console.error("[fetchCardDocumentId] HTTP error", res.status, await res.text());
        return "";
    }
    const json = await res.json();
    if (json?.error) console.error("[fetchCardDocumentId] Backend error", json.error);
    return json?.data?.[0]?.documentId ?? "";
};

export const fetchUserDocumentId = async (userId: number): Promise<string> => {
    const res = await fetch(
        `${BACKEND_URL}/api/users/${userId}`,
        { headers: strapiHeaders, credentials: "include" },
    );
    if (!res.ok) {
        console.error("[fetchUserDocumentId] HTTP error", res.status, await res.text());
        return "";
    }
    const json = await res.json();
    if (json?.error) console.error("[fetchUserDocumentId] Backend error", json.error);
    return json?.documentId ?? "";
};

export const saveRate = async ({
    cardDocumentId,
    userDocumentId,
    rateDate,
    totalWeight,
    answers,
    isDeviationReport,
    existingRateDocumentId,
}: SaveRateParams): Promise<{ id: number; documentId: string } | null> => {
    const body = {
        data: {
            Mt5Way: { connect: [{ documentId: cardDocumentId }] },
            RateUser: { connect: [{ documentId: userDocumentId }] },
            RateDate: rateDate,
            TotalWeight: totalWeight,
            ...(isDeviationReport ? { AnswersDeviation: answers } : { Answers: answers }),
        },
    };

    const url = existingRateDocumentId
        ? `${BACKEND_URL}/api/mt-5-why-rates/${existingRateDocumentId}`
        : `${BACKEND_URL}/api/mt-5-why-rates`;

    const res = await fetch(url, {
        method: existingRateDocumentId ? "PUT" : "POST",
        headers: strapiHeaders,
        credentials: "include",
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        console.error("[saveRate] HTTP error", res.status, await res.text());
        return null;
    }
    const json = await res.json();
    if (json?.error) console.error("[saveRate] Backend error", json.error);
    return { id: json?.data?.id, documentId: json?.data?.documentId };
};
